// Il wallet da telefono, restando nel browser normale.
//
// Due pezzi, presi ciascuno da dove funziona:
//
//   COLLEGAMENTO → deeplink `connect` di Phantom. Il gioco resta in Chrome,
//   Phantom si apre come app, l'utente approva e si torna qui con l'indirizzo.
//   Questa tappa ha sempre funzionato.
//
//   FIRMA → il browser interno di Phantom, e non il deeplink `signMessage`.
//   Quello risponde `-32603 Unexpected error`, un guasto interno di Phantom
//   aperto dal 2024. L'abbiamo inseguito escludendo, uno per uno: formato della
//   richiesta, dimensione, contenuto del messaggio, gesto dell'utente, identità
//   dell'app nei metadati, indirizzo di ritorno, cluster dichiarato, fino a
//   confrontare la nostra richiesta con l'app dimostrativa ufficiale di
//   Phantom. Nessuna differenza rimasta, e il rifiuto resta.
//
// La firma non è facoltativa: `/api/entry` richiede l'autenticazione, quindi
// senza token non si ottiene il biglietto e non si entra in nessuna arena.
//
// Il passaggio del token dal browser di Phantom a questo è in
// lib/consegnaSessione, dove c'è anche il modello di sicurezza.
//
// PERCHÉ OGNI SALTO ASPETTA UN TOCCO
//
// iOS e Android aprono un'altra app solo se il salto nasce da un gesto
// dell'utente: un `location.href` eseguito da solo al caricamento della pagina
// viene soppresso, e l'unica traccia è il ritorno immediato al browser.

import { useState, useEffect, useCallback } from 'react';
import { gestisciRitorno, walletCollegato, connetti, scollega } from './phantomDeeplink';
import { getToken } from './authClient';
import {
  apriPhantomPerFirmare,
  riscuotiToken,
  segretoInAttesa,
  dimenticaSegreto,
} from './consegnaSessione';

export default function usePhantomConnect() {
  const [wallet, setWallet] = useState(() => walletCollegato());
  const [authed, setAuthed] = useState(() => {
    const w = walletCollegato();
    return Boolean(w && getToken(w));
  });
  const [errore, setErrore] = useState(null);
  const [inCorso, setInCorso] = useState(false);

  // Il ritorno dal deeplink di collegamento.
  useEffect(() => {
    const esito = gestisciRitorno();
    if (!esito) return;

    if (esito.errore) {
      // Rifiuto dell'utente compreso: è una risposta, non un guasto.
      setErrore(esito.codice ? `${esito.errore} (${esito.codice})` : esito.errore);
      setInCorso(false);
      return;
    }

    if (esito.tappa === 'connect' && esito.wallet) {
      setWallet(esito.wallet);
      setInCorso(false);
    }
  }, []);

  // Il ritiro della sessione consegnata. Va tentato al rientro in primo piano:
  // è esattamente quello che succede tornando qui dal browser di Phantom, e la
  // pagina NON viene ricaricata — senza questo, nessuno se ne accorgerebbe.
  const riscuoti = useCallback(() => {
    if (!segretoInAttesa()) return;
    riscuotiToken()
      .then((indirizzo) => {
        if (!indirizzo) return;
        setWallet(indirizzo);
        setAuthed(true);
        setInCorso(false);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    riscuoti();
    const suRitorno = () => {
      if (document.visibilityState === 'visible') riscuoti();
    };
    document.addEventListener('visibilitychange', suRitorno);
    window.addEventListener('focus', suRitorno);
    return () => {
      document.removeEventListener('visibilitychange', suRitorno);
      window.removeEventListener('focus', suRitorno);
    };
  }, [riscuoti]);

  // Le due funzioni qui sotto vanno chiamate SOLO dal gestore di un tocco.
  const collega = useCallback(() => {
    setErrore(null);
    setInCorso(true);
    connetti();
  }, []);

  const firma = useCallback(() => {
    setErrore(null);
    setInCorso(true);
    window.location.href = apriPhantomPerFirmare();
  }, []);

  const disconnetti = useCallback(() => {
    scollega();
    dimenticaSegreto();
    setWallet(null);
    setAuthed(false);
    setErrore(null);
    setInCorso(false);
  }, []);

  return {
    wallet,
    authed,
    errore,
    inCorso,
    // Vero quando manca solo la firma: il wallet c'è, il token no.
    daFirmare: Boolean(wallet) && !authed,
    collega,
    firma,
    disconnetti,
  };
}
